import React, { useEffect, useState } from "react";
import axios from "axios";
import { Compass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Read-only Phase C banner. Polls the routed-orders endpoint filtered by
 * department and shows a count + a small expandable list of order numbers.
 * Hidden when total === 0 OR endpoint returns 401/403/no items, so users who
 * cannot access it see nothing.
 *
 * Phase C is gated by a backend feature flag. While the flag is OFF the
 * collection of routed orders is empty, so this banner self-hides.
 */
const PhaseCBanner = ({ department, testIdPrefix = "phase-c" }) => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const url = `${API}/admin/phase-c/routed-orders?department=${encodeURIComponent(department)}&limit=20`;
        const res = await axios.get(url);
        if (!cancelled) setData(res.data || { total: 0, items: [] });
      } catch (_e) {
        if (!cancelled) setData({ total: 0, items: [] });
      }
    };
    fetchData();
    const id = setInterval(fetchData, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [department]);

  if (!data || data.total === 0) return null;

  return (
    <div
      className="bg-[#FFF8E8] border border-[#D1A054]/40 rounded-lg p-3 flex flex-col gap-2"
      data-testid={`${testIdPrefix}-phase-c-banner`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Compass className="h-4 w-4 text-[#7A6535]" />
        <span className="text-xs font-semibold text-[#7A6535] uppercase">
          Phase C — Reappeared orders routed to {department}
        </span>
        <span
          className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#D1A054]/30 text-[#7A6535]"
          data-testid={`${testIdPrefix}-phase-c-count`}
        >
          {data.total}
        </span>
        <button
          onClick={() => setOpen(o => !o)}
          className="text-xs font-medium text-[#7A6535] underline hover:text-[#5C4925]"
          data-testid={`${testIdPrefix}-phase-c-toggle`}
        >
          {open ? "Hide list" : "View list"}
        </button>
      </div>
      {open && (
        <div className="bg-white rounded border border-[#D1A054]/30 max-h-[240px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FFF8E8] text-[#7A6535] uppercase text-[10px]">
              <tr>
                <th className="text-left px-2 py-1.5">Order #</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-left px-2 py-1.5">Reason</th>
                <th className="text-left px-2 py-1.5">Reapp.</th>
                <th className="text-left px-2 py-1.5">Address</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((it, i) => (
                <tr
                  key={it.order_number || i}
                  className="border-t border-gray-100"
                  data-testid={`${testIdPrefix}-phase-c-row-${i}`}
                >
                  <td className="px-2 py-1.5 font-semibold text-gray-800">{it.order_number || "—"}</td>
                  <td className="px-2 py-1.5 text-gray-600">{it.status || "—"}</td>
                  <td className="px-2 py-1.5 text-gray-500 max-w-[260px] truncate" title={it.phase_c_route_reason || ""}>
                    {it.phase_c_route_reason || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-gray-600">{it.total_reappearances ?? 0}</td>
                  <td className="px-2 py-1.5 text-gray-500 max-w-[200px] truncate">
                    {[it.address, it.city, it.state].filter(Boolean).join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.total > (data.items || []).length && (
            <div className="px-2 py-1.5 text-[10px] text-gray-400 bg-[#FFF8E8] border-t border-[#D1A054]/20">
              Showing first {(data.items || []).length} of {data.total} orders.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhaseCBanner;
